/**
 * EU VAT number validation based on VIES country-specific formats.
 * @see https://ec.europa.eu/taxation_customs/vies/faq.html
 */
const VAT_PATTERNS: Record<string, RegExp> = {
  AT: /^U\d{8}$/,
  BE: /^0?\d{9,10}$/,
  BG: /^\d{9,10}$/,
  CY: /^\d{8}[A-Z]$/,
  CZ: /^\d{8,10}$/,
  DE: /^\d{9}$/,
  DK: /^\d{8}$/,
  EE: /^\d{9}$/,
  EL: /^\d{9}$/,
  ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/,
  FI: /^\d{8}$/,
  FR: /^[A-HJ-NP-Z0-9]{2}\d{9}$/,
  GB: /^(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
  HR: /^\d{11}$/,
  HU: /^\d{8}$/,
  // 7 cifre + ét eller to bogstaver, eller det gamle format med bogstav/+/* på 2. position
  IE: /^(\d{7}[A-Z]{1,2}|\d[A-Z*+]\d{5}[A-Z])$/,
  IT: /^\d{11}$/,
  LT: /^(\d{9}|\d{12})$/,
  LU: /^\d{8}$/,
  LV: /^\d{11}$/,
  MT: /^\d{8}$/,
  NL: /^\d{9}B\d{2}$/,
  PL: /^\d{10}$/,
  PT: /^\d{9}$/,
  RO: /^\d{2,10}$/,
  SE: /^\d{12}$/,
  SI: /^\d{8}$/,
  SK: /^\d{10}$/,
  XI: /^(\d{9}|\d{12})$/,
};

export const EU_VAT_VALIDATION_MESSAGE =
  "Momsnummer skal være et gyldigt momsnummer for landet (landekode + nummer, fx DE123456789, CHE116281710 eller NO923609016)";

/**
 * Kendte momsnummerformater uden for EU. De er ikke EU-momsnumre (ingen omvendt betalingspligt), men skal kunne
 * registreres i AIKA og sendes til BC, som selv afgør momsklassifikationen ud fra debitorens landekode.
 * Spejler backendens EuVatNumberValidation.NonEuVatPatterns.
 */
const NON_EU_VAT_PATTERNS: Record<string, RegExp> = {
  CH: /^E?\d{9}(MWST|TVA|IVA)?$/, // fx CHE-116.281.710 MWST
  NO: /^\d{9}(MVA)?$/, // fx NO 923 609 016 MVA
};

/** Samme regel og tekst som backendens EuVatNumberValidation.ErPaakraevetForFirma. */
export const VAT_REQUIRED_MESSAGE =
  "Firmaer i andre EU-lande uden dansk CVR-nummer skal have et VAT-nummer (udenlandsk virksomheds-id)";

/** EU-medlemslande (ISO 3166-1 alpha-2; Grækenland både som GR og VIES-koden EL). */
const EU_COUNTRY_CODES = new Set([
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "EL",
  "GR",
  "ES",
  "FI",
  "FR",
  "HR",
  "HU",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
]);

export const isEuCountryOutsideDenmark = (
  landekode: string | null | undefined,
): boolean => {
  const code = landekode?.trim().toUpperCase();
  return !!code && code !== "DK" && EU_COUNTRY_CODES.has(code);
};

/**
 * Et firma i et andet EU-land end Danmark uden dansk CVR-nummer skal have et VAT-nummer — ellers kan BC ikke
 * momsklassificere debitoren. Firmaer uden for EU (US, CH, NO, FO …) har ikke nødvendigvis et VAT-nummer.
 */
export const requiresVatNumber = (firma: {
  landekode: string | null | undefined;
  cvrNr: number | string | null | undefined;
  udenlandskVirksomhedsId: string | null | undefined;
}): boolean =>
  !isDanishCvrNumber(firma.cvrNr) &&
  !firma.udenlandskVirksomhedsId?.trim() &&
  isEuCountryOutsideDenmark(firma.landekode);

export const normalizeVatNumber = (input: string): string =>
  input.replace(/[\s.-]/g, "").toUpperCase();

export const isDanishCvrNumber = (
  value: number | string | null | undefined,
): boolean => {
  if (value === null || value === undefined || value === "") return false;
  return typeof value === "number" || /^\d{8}$/.test(String(value));
};

const matchesCountryFormat = (
  input: string,
  patterns: Record<string, RegExp>,
  maxLength: number,
): boolean => {
  const normalized = normalizeVatNumber(input);
  if (normalized.length < 4 || normalized.length > maxLength) return false;

  const countryCode = normalized.substring(0, 2);
  const nationalPart = normalized.substring(2);
  const pattern = patterns[countryCode];

  if (!pattern) return false;
  return pattern.test(nationalPart);
};

export const isEuVatNumber = (input: string): boolean =>
  matchesCountryFormat(input, VAT_PATTERNS, 14);

/** Gyldigt momsnummer for et land vi kender formatet på — EU eller ikke-EU (fx Schweiz og Norge). */
export const isKnownVatNumber = (input: string): boolean =>
  isEuVatNumber(input) || matchesCountryFormat(input, NON_EU_VAT_PATTERNS, 20);

export const getVatValidationError = (input: string): string => {
  const normalized = normalizeVatNumber(input);
  if (!normalized) return EU_VAT_VALIDATION_MESSAGE;
  if (isKnownVatNumber(normalized)) return "";
  return EU_VAT_VALIDATION_MESSAGE;
};
